import { api } from '../api.js';
import { el, clear } from '../dom.js';
import { t } from '../i18n.js';

// The tag input, and the one control that decides whether the open half of the
// taxonomy still works in a year.
//
// Everything else the spec lists — normalisation, merging, the filter
// threshold — repairs fragmentation after the fact. This prevents it: someone
// typing `dan` has to be shown that `dancing` already exists *before* they
// invent `dancen`. So the suggestions are queried from the first character,
// over every tag in the database regardless of interface language, and
// creating a genuinely new word is a deliberate second step rather than a side
// effect of pressing Enter on a typo.

export const MAX_TAGS = 5;

export function tagField({ initial = [] } = {}) {
  let chosen = [...new Set(initial.map((tag) => String(tag).trim()).filter(Boolean))];

  const chips = el('ul', { class: 'tag-chips' });
  const input = el('input', {
    id: 'sf-tags',
    type: 'text',
    class: 'tag-input',
    autocomplete: 'off',
    placeholder: t.tagPlaceholder,
    'aria-describedby': 'sf-tags-hint',
  });

  // A listbox rather than a datalist: a native datalist cannot be styled, does
  // not fire on the first character in every browser, and gives no way to
  // distinguish "pick this existing tag" from "create this new word" — which
  // is the whole distinction this control exists to make.
  const list = el('ul', { class: 'tag-suggestions', role: 'listbox', id: 'sf-tags-list' });
  list.hidden = true;
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('aria-controls', 'sf-tags-list');

  const hint = el('p', { class: 'card-meta', id: 'sf-tags-hint' }, t.tagHint);

  // The input and its list share a wrapper so the list can anchor to the
  // input. .field is a grid, and an absolutely positioned grid child resolves
  // `top: auto` against the container's padding edge rather than its own
  // static position — which put the suggestions above the field instead of
  // under it.
  const node = el('div', { class: 'field tag-field' }, [
    el('label', { for: 'sf-tags' }, t.fieldTags),
    chips,
    el('div', { class: 'tag-input-wrap' }, [input, list]),
    hint,
  ]);

  paintChips();

  function paintChips() {
    clear(chips);
    for (const name of chosen) {
      const remove = el('button', {
        type: 'button',
        class: 'tag-chip-remove',
        'aria-label': `${name} ${t.remove}`,
      }, '×');
      remove.addEventListener('click', () => {
        chosen = chosen.filter((entry) => entry !== name);
        paintChips();
        input.focus();
      });
      chips.append(el('li', { class: 'tag-chip' }, [name, remove]));
    }
    input.disabled = chosen.length >= MAX_TAGS;
    hint.textContent = chosen.length >= MAX_TAGS ? t.tagLimitReached : t.tagHint;
  }

  function add(name) {
    const value = String(name).trim().toLowerCase().replace(/\s+/g, ' ');
    if (!value || chosen.includes(value) || chosen.length >= MAX_TAGS) return;
    chosen.push(value);
    paintChips();
    input.value = '';
    closeList();
  }

  function closeList() {
    list.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    clear(list);
  }

  let token = 0;
  async function refresh() {
    const query = input.value.trim();
    const mine = ++token;

    let found = [];
    try {
      found = await api.get(`/tags?q=${encodeURIComponent(query)}`);
    } catch {
      found = [];
    }
    if (mine !== token) return; // a later keystroke already answered

    const available = found.filter((tag) => !chosen.includes(tag.name));
    const exact = available.some((tag) => tag.name === query.toLowerCase());

    clear(list);
    for (const tag of available) list.append(option(tag.name, t.tagExisting));

    // Creating is offered last and labelled as what it is. Putting it first,
    // or unlabelled, is how "dancen" gets created by somebody who meant to
    // pick "dancing" from the list directly above it.
    if (query && !exact) list.append(option(query.toLowerCase(), t.tagCreate, 'tag-suggestion-new'));

    list.hidden = list.children.length === 0;
    input.setAttribute('aria-expanded', list.hidden ? 'false' : 'true');
  }

  function option(name, note, extra = '') {
    const item = el('li', { class: `tag-suggestion ${extra}`.trim(), role: 'option' }, [
      el('span', { class: 'tag-suggestion-name' }, name),
      el('span', { class: 'tag-suggestion-note' }, note),
    ]);
    item.addEventListener('mousedown', (event) => {
      event.preventDefault(); // keep focus in the input
      add(name);
    });
    return item;
  }

  input.addEventListener('input', refresh);
  input.addEventListener('focus', refresh);
  input.addEventListener('blur', () => setTimeout(closeList, 120));

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      // Never submits the form. Enter in this field means "take this word",
      // and a form that saves because somebody finished typing a tag is a
      // form that saves half-filled.
      event.preventDefault();
      const first = list.querySelector('.tag-suggestion:not(.tag-suggestion-new) .tag-suggestion-name');
      add(first ? first.textContent : input.value);
      return;
    }
    if (event.key === 'Escape' && !list.hidden) {
      event.preventDefault();
      closeList();
      return;
    }
    // Backspace on an empty field removes the last chip — the behaviour every
    // token input has, and the only way to correct a mistake without reaching
    // for the mouse.
    if (event.key === 'Backspace' && input.value === '' && chosen.length > 0) {
      chosen.pop();
      paintChips();
    }
  });

  return { node, value: () => [...chosen] };
}
